/**
 * SupplierCalendarPage — /dashboard/supplier/calendar
 *
 * Month grid view. Free suppliers can VIEW + see confirmed bookings.
 * External bookings + edit actions are gated behind Studio Pass.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, CaretLeft, CaretRight, Lock, Plus, X, CalendarBlank, Trash, Prohibit, Clock, Lightning, PaperPlaneTilt } from '@phosphor-icons/react';
import AutoAcceptPanel from '../components/AutoAcceptPanel';
import SupplierBookingLinkModal from '../components/SupplierBookingLinkModal';
import SupplierExternalBookingModal from '../components/SupplierExternalBookingModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WEEK_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function monthKey(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; }
function daysIn(y, m) { return new Date(y, m + 1, 0).getDate(); }
function iso(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
function fmtLong(d) { return d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); }

// Tight status → visual config. Order matters in the legend.
const STATUS_STYLE = {
  booked:    { dot: 'bg-emerald-400',    tint: 'bg-emerald-500/20 border-emerald-400/40 text-white',   label: 'Booked' },
  blocked:   { dot: 'bg-red-500',        tint: 'bg-red-500/15 border-red-400/40 text-white',             label: 'Blocked' },
  external:  { dot: 'bg-white/60',       tint: 'bg-white/10 border-white/20 text-white',                label: 'External' },
  partial:   { dot: 'bg-amber-400',      tint: 'bg-amber-400/10 border-amber-300/40 text-white',        label: 'Partial' },
  available: { dot: 'bg-[#B89762]/40/60',    tint: '',                                                       label: 'Available' },
};

// Canonical service chips used on the external-booking form.
const EXT_SERVICE_OPTIONS = [
  { id: 'dj', label: 'DJ / Band', emoji: '🎧' },
  { id: 'photographer', label: 'Photographer', emoji: '📷' },
  { id: 'videographer', label: 'Videographer', emoji: '🎥' },
  { id: 'photobooth', label: 'Photo Booth', emoji: '📸' },
  { id: 'caterer', label: 'Caterer', emoji: '🍽️' },
  { id: 'florist', label: 'Florist', emoji: '🌼' },
  { id: 'venue', label: 'Venue', emoji: '🏛️' },
  { id: 'cake_maker', label: 'Cake Maker', emoji: '🎂' },
  { id: 'transport', label: 'Transport', emoji: '🚗' },
  { id: 'decorator', label: 'Decorator', emoji: '🎨' },
  { id: 'wedding_planner', label: 'Wedding Planner', emoji: '✨' },
  { id: 'hair_makeup', label: 'Hair & Makeup', emoji: '💅' },
];

// Partial-label → short human-readable window for the day cell
const PARTIAL_BLURB = {
  morning:   'Morning',
  afternoon: 'Afternoon',
  evening:   'Evening',
};

const LONG_PRESS_MS = 400;

const SupplierCalendarPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Honour `?month=YYYY-MM` so emailed deep-links (notification
  // emails, booking ledger references) land on the right page of
  // the calendar without forcing the supplier to chevron forward.
  const [cursor, setCursor] = useState(() => {
    const qm = (searchParams.get('month') || '').trim();
    const match = /^(\d{4})-(\d{2})$/.exec(qm);
    if (match) {
      const y = parseInt(match[1], 10);
      const m = parseInt(match[2], 10);
      if (m >= 1 && m <= 12) return new Date(y, m - 1, 1);
    }
    const d = new Date(); d.setDate(1); return d;
  });
  const [days, setDays] = useState({}); // keyed by YYYY-MM-DD
  const [meta, setMeta] = useState(null);
  const [nextBooking, setNextBooking] = useState(null);
  const [isPro, setIsPro] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  // Booking link composer — opens from the day modal, prefilled
  // with the selected date so the modal can hint "for {date}" copy.
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkPrefillDate, setLinkPrefillDate] = useState(null);
  const [externalModalOpen, setExternalModalOpen] = useState(false);
  const [externalPrefillDate, setExternalPrefillDate] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [addForm, setAddForm] = useState(null); // {date, title}

  const month = monthKey(cursor);

  const refresh = useCallback(async (m) => {
    try {
      const [cal, nxt, quota] = await Promise.all([
        axios.get(`${API}/calendar`, { params: { month: m } }),
        axios.get(`${API}/calendar/next`),
        axios.get(`${API}/suppliers/me/chat-quota`),
      ]);
      const map = {};
      (cal.data.days || []).forEach((d) => { map[d.date] = d; });
      setDays(map);
      setMeta(cal.data.meta || null);
      setNextBooking(nxt.data.next);
      setIsPro(!!quota.data.is_pro);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error(err?.response?.data?.detail || 'Could not load calendar');
      }
    }
  }, [navigate]);

  useEffect(() => {
    setLoading(true);
    refresh(month).finally(() => setLoading(false));
  }, [month, refresh]);

  const grid = useMemo(() => {
    const y = cursor.getFullYear();
    const m = cursor.getMonth();
    const total = daysIn(y, m);
    const firstWeekday = (new Date(y, m, 1).getDay() + 6) % 7; // Mon=0
    const cells = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let d = 1; d <= total; d++) cells.push(new Date(y, m, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const shift = (delta) => {
    const next = new Date(cursor);
    next.setMonth(next.getMonth() + delta);
    setCursor(next);
  };

  const onDayClick = (d) => {
    if (!d) return;
    setSelectedDate(d);
  };

  // Long-press quick-block: suppliers use this constantly, so optimise for speed.
  // Touch + mouse both supported. Cancel on move/leave to avoid scroll-misfires.
  const longPressTimer = useRef(null);
  const longPressFired = useRef(false);
  const startLongPress = (d) => {
    if (!d || !isPro) return;
    longPressFired.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressFired.current = true;
      toggleBlock(d);
    }, LONG_PRESS_MS);
  };
  const cancelLongPress = () => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
    longPressTimer.current = null;
  };
  const handleCellClick = (d) => {
    // If the long-press already fired, swallow the subsequent click.
    if (longPressFired.current) {
      longPressFired.current = false;
      return;
    }
    onDayClick(d);
  };

  const toggleBlock = async (d) => {
    const date = iso(d);
    const status = days[date]?.status;
    try {
      if (status === 'blocked') {
        await axios.delete(`${API}/calendar/block/${date}`);
        toast.success('Unblocked');
      } else {
        if (status === 'booked') {
          toast.error("Can't block a booked day");
          return;
        }
        await axios.post(`${API}/calendar/block`, { date });
        toast.success('Blocked');
      }
      refresh(month);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail?.error === 'upgrade_required') {
        setShowUpgrade(true);
      } else {
        toast.error(detail?.message || detail || 'Could not update');
      }
    }
  };

  const setPartial = async (label) => {
    if (!selectedDate) return;
    try {
      await axios.post(`${API}/calendar/partial`,
                       { date: iso(selectedDate), label });
      toast.success(`Partial (${label}) set`);
      setSelectedDate(null);
      refresh(month);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail?.error === 'upgrade_required') {
        setShowUpgrade(true);
      } else {
        toast.error(detail?.message || detail || 'Could not set partial');
      }
    }
  };

  const clearPartial = async () => {
    if (!selectedDate) return;
    try {
      await axios.delete(`${API}/calendar/partial/${iso(selectedDate)}`);
      toast.success('Partial cleared');
      setSelectedDate(null);
      refresh(month);
    } catch {
      toast.error('Could not clear');
    }
  };

  const onRequestAddExternal = (date) => {
    if (!isPro) {
      setShowUpgrade(true);
      return;
    }
    setAddForm({
      date: iso(date),
      title: '',
      venue: '',
      start_time: '',
      end_time: '',
      services_required: [],
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      notes: '',
    });
  };

  const saveExternal = async () => {
    if (!addForm?.date) return;
    try {
      await axios.post(`${API}/external-bookings`, {
        date: addForm.date,
        title: addForm.title || null,
        venue: addForm.venue || null,
        start_time: addForm.start_time || null,
        end_time: addForm.end_time || null,
        services_required: (addForm.services_required || []).length ? addForm.services_required : null,
        customer_name: addForm.customer_name || null,
        customer_email: addForm.customer_email || null,
        customer_phone: addForm.customer_phone || null,
        notes: addForm.notes || null,
      });
      toast.success('External booking added');
      setAddForm(null);
      setSelectedDate(null);
      refresh(month);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail?.error === 'upgrade_required') {
        setShowUpgrade(true);
        setAddForm(null);
      } else {
        toast.error(detail?.message || detail || 'Could not add booking');
      }
    }
  };

  const toggleExternalService = (id) => {
    setAddForm((prev) => {
      if (!prev) return prev;
      const has = (prev.services_required || []).includes(id);
      return {
        ...prev,
        services_required: has
          ? prev.services_required.filter((s) => s !== id)
          : [...(prev.services_required || []), id],
      };
    });
  };

  const deleteExternal = async (ext_id) => {
    try {
      await axios.delete(`${API}/external-bookings/${ext_id}`);
      refresh(month);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove');
    }
  };

  const selectedKey = selectedDate ? iso(selectedDate) : null;
  const selectedDay = selectedKey ? days[selectedKey] : null;

  return (
    <div className="min-h-screen bg-[#0F1014] text-white" data-testid="supplier-calendar">
      <div className="max-w-4xl mx-auto px-5 py-6">
        <button
          onClick={() => navigate('/dashboard/supplier')}
          className="flex items-center gap-1 text-white/60 hover:text-white text-sm mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft size={14} weight="bold" /> Back to dashboard
        </button>

        <AutoAcceptPanel />

        {/* Next booking strip */}
        {nextBooking && (
          <div
            className="rounded-2xl p-4 mb-5 border border-[#B89762]/40/20"
            style={{ background: 'linear-gradient(135deg, rgba(184,151,98,0.18), rgba(194,157,82,0.10))' }}
            data-testid="next-booking-strip"
          >
            <p className="text-[10px] font-black uppercase tracking-[2px] text-[#C29D52]/85 mb-1">Next Booking</p>
            <div className="flex items-end justify-between gap-3">
              <div>
                <p className="font-black text-lg leading-tight">{nextBooking.title}</p>
                <p className="text-white/50 text-xs mt-0.5">
                  {new Date(nextBooking.date).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })}
                  {nextBooking.days_to_go >= 0 && ` · ${nextBooking.days_to_go === 0 ? 'Today' : `${nextBooking.days_to_go} day${nextBooking.days_to_go === 1 ? '' : 's'} to go`}`}
                </p>
              </div>
              {nextBooking.kind === 'platform' && (
                <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-emerald-400 text-[#0F1014]">BOOKED</span>
              )}
            </div>
          </div>
        )}

        {/* Calendar header */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-black" data-testid="calendar-title">
            {cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
          </h1>
          <div className="flex gap-1">
            <button
              onClick={() => shift(-1)}
              className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center"
              data-testid="prev-month-btn"
            >
              <CaretLeft size={16} weight="bold" />
            </button>
            <button
              onClick={() => { const d = new Date(); d.setDate(1); setCursor(d); }}
              className="px-3 h-9 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-bold"
              data-testid="today-btn"
            >
              Today
            </button>
            <button
              onClick={() => shift(1)}
              className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center"
              data-testid="next-month-btn"
            >
              <CaretRight size={16} weight="bold" />
            </button>
          </div>
        </div>

        {/* Availability Confidence banner (Phase B) */}
        {meta && <AvailabilityConfidenceBanner meta={meta} />}

        {/* Legend */}
        <div className="flex items-center gap-3 mb-3 text-[10px] font-bold text-white/40 flex-wrap">
          <div className="flex items-center gap-1.5" data-testid="legend-booked"><span className={`w-2 h-2 rounded-full ${STATUS_STYLE.booked.dot}`}/>BOOKED</div>
          <div className="flex items-center gap-1.5" data-testid="legend-blocked"><span className={`w-2 h-2 rounded-full ${STATUS_STYLE.blocked.dot}`}/>BLOCKED</div>
          <div className="flex items-center gap-1.5" data-testid="legend-external"><span className={`w-2 h-2 rounded-full ${STATUS_STYLE.external.dot}`}/>EXTERNAL</div>
          <div className="flex items-center gap-1.5" data-testid="legend-partial"><span className={`w-2 h-2 rounded-full ${STATUS_STYLE.partial.dot}`}/>PARTIAL</div>
          <div className="flex items-center gap-1.5" data-testid="legend-available"><span className={`w-2 h-2 rounded-full ${STATUS_STYLE.available.dot}`}/>AVAILABLE</div>
        </div>

        {/* Weekday header */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {WEEK_LABELS.map((w) => (
            <div key={w} className="text-center text-[10px] font-black text-white/30 uppercase">{w}</div>
          ))}
        </div>

        {/* Month grid */}
        <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
          {loading && <div className="col-span-7 py-12 text-center text-white/40 text-sm">Loading…</div>}
          {!loading && grid.map((d, i) => {
            if (!d) return <div key={i} />;
            const k = iso(d);
            const info = days[k];
            const status = info?.status || 'available';
            const isToday = k === iso(new Date());
            const s = STATUS_STYLE[status];
            return (
              <button
                key={k}
                onClick={() => handleCellClick(d)}
                onMouseDown={() => startLongPress(d)}
                onMouseUp={cancelLongPress}
                onMouseLeave={cancelLongPress}
                onTouchStart={() => startLongPress(d)}
                onTouchEnd={cancelLongPress}
                onTouchMove={cancelLongPress}
                onContextMenu={(e) => e.preventDefault()}
                className={`aspect-square rounded-lg border text-left p-1.5 relative overflow-hidden transition
                  ${s.tint || 'border-white/5 hover:bg-white/[0.04]'}
                  ${isToday ? 'ring-1 ring-[#C29D52]/60' : ''}`}
                data-testid={`day-${k}`}
                aria-label={`${k} — ${s.label}. Long-press to toggle block.`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-bold ${status === 'available' ? 'text-white/70' : 'text-white'}`}>{d.getDate()}</span>
                  <div className="flex items-center gap-1">
                    {info?.bookings?.some((b) => b.access_issues) && (
                      <span
                        title="Access constraints on this booking"
                        aria-label="Access constraints on this booking"
                        data-testid={`access-flag-${k}`}
                        className="text-[10px] leading-none font-black text-amber-300"
                      >
                        ⚠︎
                      </span>
                    )}
                    {info?.is_instant_bookable && (
                      <Lightning
                        size={10}
                        weight="fill"
                        className="text-amber-300"
                        data-testid={`instant-bolt-${k}`}
                        aria-label="Instant book"
                      />
                    )}
                    {info && <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />}
                  </div>
                </div>
                {status === 'partial' && info?.partial && (
                  <p className="text-[9px] mt-1 font-bold text-amber-200 truncate" data-testid={`partial-blurb-${k}`}>
                    {PARTIAL_BLURB[info.partial.label] || info.partial.label} {info.partial.start_time}–{info.partial.end_time}
                  </p>
                )}
                {status === 'blocked' && (
                  <p className="text-[9px] mt-1 font-bold text-red-200">Unavailable</p>
                )}
                {info?.bookings?.[0] && status !== 'partial' && status !== 'blocked' && (
                  <p className="text-[9px] truncate opacity-80 mt-1 font-bold">{info.bookings[0].title}</p>
                )}
                {info?.bookings?.length > 1 && (
                  <p className="text-[9px] opacity-60">+{info.bookings.length - 1} more</p>
                )}
              </button>
            );
          })}
        </div>

        {/* Free-tier hint */}
        {!loading && !isPro && (
          <p className="text-center text-white/40 text-xs mt-4" data-testid="free-hint">
            Viewing calendar as a free supplier. Upgrade to manage external bookings →
          </p>
        )}
      </div>

      {/* Day detail modal */}
      {selectedDate && (
        <div
          className="fixed inset-0 z-40 flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-6"
          onClick={() => setSelectedDate(null)}
          data-testid="day-detail-modal"
        >
          <div
            className="w-full max-w-md rounded-t-2xl sm:rounded-2xl p-6 bg-[#15161B] border-t sm:border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[10px] font-black text-[#C29D52] uppercase tracking-wider mb-0.5">
                  {selectedDay ? STATUS_STYLE[selectedDay.status].label : 'Available'}
                </p>
                <h3 className="text-lg font-black">{fmtLong(selectedDate)}</h3>
              </div>
              <button
                onClick={() => setSelectedDate(null)}
                className="text-white/40 hover:text-white"
                data-testid="day-close-btn"
              >
                <X size={18} weight="bold" />
              </button>
            </div>

            {selectedDay?.bookings?.length > 0 ? (
              <ul className="space-y-2 mb-5" data-testid="day-bookings-list">
                {selectedDay.bookings.map((b) => (
                  <li
                    key={b.id}
                    className="rounded-lg p-3 bg-white/[0.04] border border-white/10"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${b.kind === 'platform' ? 'bg-emerald-500/30' : 'bg-white/10'}`}>
                        <CalendarBlank size={14} weight="fill" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm truncate">{b.title}</p>
                        <p className="text-white/40 text-xs truncate">
                          {b.kind === 'platform'
                            ? (b.customer_name || 'Customer')
                            : (b.customer_name || 'External')}
                          {b.start_time || b.end_time
                            ? ` · ${b.start_time || '—'}–${b.end_time || '—'}`
                            : ''}
                        </p>
                      </div>
                      {b.kind === 'external' && isPro && (
                        <button
                          onClick={() => deleteExternal(b.id)}
                          className="text-white/30 hover:text-red-400"
                          data-testid={`delete-external-${b.id}`}
                        >
                          <Trash size={14} weight="bold" />
                        </button>
                      )}
                    </div>
                    {/* External booking detail rail — only when we have any */}
                    {b.kind === 'external' && (b.venue || b.venue_address || b.access_issues || (b.services_required && b.services_required.length) || b.customer_email || b.customer_phone || b.notes) && (
                      <div className="mt-2.5 pl-11 space-y-1 text-[11px] text-white/65" data-testid={`external-details-${b.id}`}>
                        {b.venue && <p>📍 {b.venue}</p>}
                        {b.venue_address && <p className="text-white/55 pl-4 italic">{b.venue_address}</p>}
                        {b.access_issues && (
                          <p className="text-amber-200 font-bold" data-testid={`access-issues-${b.id}`}>
                            ⚠︎ Access: {b.access_issues}
                          </p>
                        )}
                        {b.services_required && b.services_required.length > 0 && (
                          <p>🎯 {b.services_required.map((s) => (EXT_SERVICE_OPTIONS.find((o) => o.id === s) || { label: s }).label).join(' · ')}</p>
                        )}
                        {b.customer_email && <p>✉️ <a href={`mailto:${b.customer_email}`} className="text-[#C29D52]/85 hover:text-[#F4E9D2]">{b.customer_email}</a></p>}
                        {b.customer_phone && <p>📞 <a href={`tel:${b.customer_phone}`} className="text-[#C29D52]/85 hover:text-[#F4E9D2]">{b.customer_phone}</a></p>}
                        {b.notes && <p className="italic text-white/45">"{b.notes}"</p>}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : selectedDay?.status === 'blocked' ? (
              <p className="text-red-200 text-sm mb-5 text-center py-3" data-testid="day-blocked-empty">
                You've marked this day unavailable.
              </p>
            ) : selectedDay?.status === 'partial' && selectedDay?.partial ? (
              <p className="text-amber-200 text-sm mb-5 text-center py-3" data-testid="day-partial-summary">
                Partial availability · {selectedDay.partial.label} ({selectedDay.partial.start_time}–{selectedDay.partial.end_time})
              </p>
            ) : (
              <p className="text-white/40 text-sm mb-5 text-center py-3">Nothing booked on this day.</p>
            )}

            {/* Tri-mode quick actions — only shown on days the supplier can still edit */}
            {selectedDay?.status !== 'booked' && (
              <div className="space-y-2">
                {/* Send Booking Link ✦ — same surface as Bookings page,
                    but contextual to the day the supplier just tapped */}
                <button
                  onClick={() => {
                    setLinkPrefillDate(fmtLong(selectedDate));
                    setLinkModalOpen(true);
                  }}
                  data-testid="day-send-booking-link"
                  className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
                  style={{
                    background: 'linear-gradient(135deg, rgba(194,157,82,0.22), rgba(194,157,82,0.08))',
                    border: '1px solid rgba(194,157,82,0.45)',
                    color: '#F4E9D2', fontStyle: 'italic',
                    fontFamily: 'Georgia, serif',
                  }}
                >
                  <PaperPlaneTilt size={14} weight="fill" /> Send Booking Link ✦
                </button>
                {/* Create External Booking ✦ — supplier-authored
                    booking on the client's behalf, for off-platform
                    enquiries (phone, fayre, DM). Same surface as
                    the Bookings page, prefilled with this day. */}
                <button
                  onClick={() => {
                    setExternalPrefillDate(iso(selectedDate));
                    setExternalModalOpen(true);
                  }}
                  data-testid="day-create-external-booking"
                  className="w-full py-3 rounded-xl text-sm font-bold inline-flex items-center justify-center gap-2"
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(194,157,82,0.35)',
                    color: '#F4E9D2',
                    fontFamily: 'Georgia, serif',
                  }}
                >
                  ✦ Create External Booking
                </button>
                {selectedDay?.status === 'blocked' ? (
                  <button
                    onClick={() => toggleBlock(selectedDate)}
                    className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 bg-red-500/15 hover:bg-red-500/25 border border-red-400/30 text-red-100"
                    data-testid="unblock-day-btn"
                  >
                    <Prohibit size={14} weight="bold" /> Unblock day
                  </button>
                ) : (
                  <button
                    onClick={() => toggleBlock(selectedDate)}
                    className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10"
                    data-testid="block-day-btn"
                  >
                    {isPro ? <Prohibit size={14} weight="bold" /> : <Lock size={14} weight="fill" />}
                    {isPro ? 'Block day' : 'Block day (Studio Pass)'}
                  </button>
                )}

                {selectedDay?.status === 'partial' ? (
                  <button
                    onClick={clearPartial}
                    className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 bg-amber-400/15 hover:bg-amber-400/25 border border-amber-300/30 text-amber-100"
                    data-testid="clear-partial-btn"
                  >
                    <Clock size={14} weight="bold" /> Clear partial
                  </button>
                ) : (
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-wider text-white/40 mb-1.5 mt-3">Set partial availability</p>
                    <div className="grid grid-cols-3 gap-2">
                      {['morning', 'afternoon', 'evening'].map((slot) => (
                        <button
                          key={slot}
                          onClick={() => setPartial(slot)}
                          className="py-2 rounded-lg text-xs font-bold capitalize bg-white/5 hover:bg-amber-400/15 border border-white/10 hover:border-amber-300/40"
                          data-testid={`partial-${slot}-btn`}
                        >
                          {isPro ? slot : <Lock size={10} weight="fill" className="inline mr-1" />}
                          {!isPro ? slot : null}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => onRequestAddExternal(selectedDate)}
                  className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10"
                  data-testid="add-external-btn"
                >
                  {isPro ? <Plus size={14} weight="bold" /> : <Lock size={14} weight="fill" />}
                  {isPro ? 'Add external booking' : 'Add external (Studio Pass)'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Upgrade lightbox */}
      {showUpgrade && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
          onClick={() => setShowUpgrade(false)}
          data-testid="calendar-upgrade-modal"
        >
          <div className="max-w-sm w-full rounded-2xl p-6 bg-[#15161B] border border-white/10" onClick={(e) => e.stopPropagation()}>
            <Lock size={24} weight="fill" className="mb-3 text-[#C29D52]" />
            <h3 className="font-black text-lg mb-1">Stay fully booked, never double-book</h3>
            <p className="text-white/60 text-sm mb-5">
              Add your external events and manage your availability in one place. Upgrade to Studio Pass to unlock your calendar.
            </p>
            <button
              onClick={() => navigate('/studio-pass')}
              className="w-full py-3 rounded-xl text-sm font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
              data-testid="calendar-upgrade-cta"
            >
              Upgrade to Studio Pass
            </button>
            <button
              onClick={() => setShowUpgrade(false)}
              className="w-full py-2 mt-2 text-xs text-white/40 hover:text-white/80"
            >
              Not now
            </button>
          </div>
        </div>
      )}

      {/* Add-external form */}
      {addForm && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm sm:p-6"
          onClick={() => setAddForm(null)}
          data-testid="add-external-modal"
        >
          <div
            className="max-w-lg w-full bg-[#15161B] border border-white/10 sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-5 pb-3 border-b border-white/5 flex items-start justify-between">
              <div>
                <p className="text-[11px] uppercase tracking-wider font-black text-[#C29D52]/85/80">External booking</p>
                <h3 className="font-black text-lg leading-tight mt-0.5">Add to your calendar</h3>
                <p className="text-white/50 text-xs mt-1">Key details so you&rsquo;ve got the full record — kept private to you.</p>
              </div>
              <button
                onClick={() => setAddForm(null)}
                className="text-white/40 hover:text-white -mr-2 -mt-1 p-2 text-xl"
              >
                ×
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-black text-white/60 uppercase tracking-wider mb-1">Title</label>
                  <input
                    type="text"
                    value={addForm.title}
                    onChange={(e) => setAddForm({ ...addForm, title: e.target.value })}
                    placeholder="Henderson Wedding"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B89762]/40/40"
                    data-testid="external-title-input"
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-black text-white/60 uppercase tracking-wider mb-1">Date</label>
                  <input
                    type="date"
                    value={addForm.date}
                    onChange={(e) => setAddForm({ ...addForm, date: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B89762]/40/40"
                    data-testid="external-date-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-black text-white/60 uppercase tracking-wider mb-1">Venue</label>
                <input
                  type="text"
                  value={addForm.venue}
                  onChange={(e) => setAddForm({ ...addForm, venue: e.target.value })}
                  placeholder="The Old Rectory, Brighton"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B89762]/40/40"
                  data-testid="external-venue-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-black text-white/60 uppercase tracking-wider mb-1">Start</label>
                  <input
                    type="time"
                    value={addForm.start_time}
                    onChange={(e) => setAddForm({ ...addForm, start_time: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B89762]/40/40"
                    data-testid="external-start-time"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-black text-white/60 uppercase tracking-wider mb-1">End</label>
                  <input
                    type="time"
                    value={addForm.end_time}
                    onChange={(e) => setAddForm({ ...addForm, end_time: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B89762]/40/40"
                    data-testid="external-end-time"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-black text-white/60 uppercase tracking-wider mb-1">Services</label>
                <div className="flex flex-wrap gap-1.5" data-testid="external-services">
                  {EXT_SERVICE_OPTIONS.map((opt) => {
                    const on = (addForm.services_required || []).includes(opt.id);
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => toggleExternalService(opt.id)}
                        className={`px-3 py-1.5 rounded-full text-[11px] font-bold border transition-colors ${
                          on
                            ? 'bg-[#B89762]/30/15 text-[#F4E9D2] border-[#B89762]/40/40'
                            : 'bg-white/[0.04] text-white/60 border-white/10 hover:bg-white/[0.08]'
                        }`}
                        data-testid={`external-service-${opt.id}`}
                      >
                        {opt.emoji} {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="pt-2 border-t border-white/5">
                <p className="text-[11px] font-black text-[#C29D52]/85/80 uppercase tracking-wider mb-2">Customer</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={addForm.customer_name}
                    onChange={(e) => setAddForm({ ...addForm, customer_name: e.target.value })}
                    placeholder="Name"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B89762]/40/40"
                    data-testid="external-customer-name"
                  />
                  <input
                    type="tel"
                    value={addForm.customer_phone}
                    onChange={(e) => setAddForm({ ...addForm, customer_phone: e.target.value })}
                    placeholder="Phone"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B89762]/40/40"
                    data-testid="external-customer-phone"
                  />
                </div>
                <input
                  type="email"
                  value={addForm.customer_email}
                  onChange={(e) => setAddForm({ ...addForm, customer_email: e.target.value })}
                  placeholder="Email"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm mt-3 focus:outline-none focus:border-[#B89762]/40/40"
                  data-testid="external-customer-email"
                />
              </div>

              <div>
                <label className="block text-[11px] font-black text-white/60 uppercase tracking-wider mb-1">Notes (optional)</label>
                <textarea
                  value={addForm.notes}
                  onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })}
                  placeholder="Deposit details, parking, dietary notes…"
                  rows={2}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B89762]/40/40 resize-none"
                  data-testid="external-notes-input"
                />
              </div>
            </div>

            <div className="border-t border-white/5 px-5 py-3 flex gap-2">
              <button
                onClick={saveExternal}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
                data-testid="save-external-btn"
              >
                Save booking
              </button>
              <button
                onClick={() => setAddForm(null)}
                className="px-4 py-2.5 rounded-xl text-sm font-bold bg-white/5 border border-white/10 hover:bg-white/10"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <SupplierBookingLinkModal
        open={linkModalOpen}
        onClose={() => setLinkModalOpen(false)}
        prefillDate={linkPrefillDate}
      />
      <SupplierExternalBookingModal
        open={externalModalOpen}
        onClose={() => setExternalModalOpen(false)}
        prefillDate={externalPrefillDate}
        onCreated={() => {
          // Re-pull the calendar so the new booking row appears
          // immediately on the day-cell.
          refresh(month);
          setExternalModalOpen(false);
          setSelectedDate(null);
        }}
      />
    </div>
  );
};

export default SupplierCalendarPage;

// ═════════════════════════════════════════════════════════
// AvailabilityConfidenceBanner
// Language-first display — users see "Highly reliable" / "Getting there" /
// "Low confidence" BEFORE the raw %. The number is context, not a verdict.
//
// Bands (matches Phase B spec):
//   0.00 – 0.30   → Low confidence        (amber)
//   0.30 – 0.70   → Getting there         (sky)
//   0.70 – 1.00   → Highly reliable       (emerald)
//
// The meta block already carries the floor (confidence=0 when explicit_days<3)
// so we trust it here — no re-computation client-side.
// ═════════════════════════════════════════════════════════

const CONFIDENCE_BANDS = [
  { min: 0.70, label: 'Highly reliable',  tone: 'emerald',
    hint: "You're ranking higher — customers trust your availability" },
  { min: 0.30, label: 'Getting there',    tone: 'sky',
    hint: 'Fill a few more days to unlock priority ranking' },
  { min: 0.00, label: 'Low confidence',   tone: 'amber',
    hint: 'Set availability on more dates so customers can book you' },
];

const TONE_STYLE = {
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-400/30', text: 'text-emerald-200', dot: 'bg-emerald-400' },
  sky:     { bg: 'bg-sky-500/10',     border: 'border-sky-400/30',     text: 'text-sky-200',     dot: 'bg-sky-400' },
  amber:   { bg: 'bg-amber-500/10',   border: 'border-amber-400/30',   text: 'text-amber-200',   dot: 'bg-amber-400' },
};

const AvailabilityConfidenceBanner = ({ meta }) => {
  const score = typeof meta.confidence === 'number' ? meta.confidence : 0;
  const band = CONFIDENCE_BANDS.find((b) => score >= b.min) || CONFIDENCE_BANDS[CONFIDENCE_BANDS.length - 1];
  const tone = TONE_STYLE[band.tone];
  const pct = Math.round(score * 100);
  return (
    <div
      className={`rounded-xl border ${tone.bg} ${tone.border} px-3 py-2.5 mb-3 flex items-center gap-3`}
      data-testid="confidence-banner"
      role="status"
    >
      <span className={`w-2 h-2 rounded-full ${tone.dot}`} data-testid="confidence-dot" />
      <div className="flex-1 min-w-0">
        <p className={`text-[11px] font-black uppercase tracking-wider ${tone.text}`} data-testid="confidence-label">
          {band.label}
        </p>
        <p className="text-white/50 text-[11px] mt-0.5 truncate">{band.hint}</p>
      </div>
      <span className={`text-sm font-black ${tone.text}`} data-testid="confidence-pct">
        {pct}%
      </span>
    </div>
  );
};
